import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleButton } from "@/pages/Login";
import { Leaf, Loader2 } from "lucide-react";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isVault = params.get("plan") === "vault";
  const ref = params.get("ref");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password, ref);
      navigate(isVault || ref ? "/vault" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="h-10 w-10 rounded-xl bg-[#114A31] flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading text-xl font-semibold text-[#1C1C1C]">ScorpPath</span>
        </Link>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl shadow-sm p-8">
          <h1 className="font-heading text-2xl font-medium text-[#1C1C1C]">Create your account</h1>
          <p className="text-sm text-[#595D5A] mt-1">Start your LLC to S-Corp conversion today.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-[#1C1C1C]">Full name</Label>
              <Input
                id="name" required value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                placeholder="Jordan Lee"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#1C1C1C]">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                placeholder="you@business.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1C1C1C]">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <p className="text-sm text-[#EF4444]" data-testid="register-error">{error}</p>
            )}
            <Button
              type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full rounded-xl py-6 bg-[#114A31] hover:bg-[#1A6B46] text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-[#595D5A]">
            <span className="h-px flex-1 bg-[#E5E5E5]" /> or <span className="h-px flex-1 bg-[#E5E5E5]" />
          </div>
          <GoogleButton label="Sign up with Google" />

          <p className="mt-6 text-center text-sm text-[#595D5A]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#114A31] font-medium hover:underline" data-testid="go-login-link">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
